import Link from "next/link";
import Image from "next/image";
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowRight, CheckCircle2, Play } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  getHeroYoutubeEmbedUrl,
  HOME_H1,
  HERO_VIDEO_POSTER,
  HERO_YOUTUBE_WATCH_URL,
  PRIMARY_CTA_HREF,
  PRIMARY_CTA_LABEL,
  SECONDARY_CTA_HREF,
  SECONDARY_CTA_LABEL,
} from "./homeContent";

const HERO_PROOF = [
  "Agenda, clientes e equipe em um só lugar",
  "Teste grátis por 7 dias",
  "Agendamento online para seus clientes",
];

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return reduced;
}

function HeroDemoMedia() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    if (prefersReducedMotion) return;
    setIsPlaying(true);
  }, [prefersReducedMotion]);

  if (prefersReducedMotion) {
    return (
      <Box as="a" href={HERO_YOUTUBE_WATCH_URL} target="_blank" rel="noopener noreferrer">
        <Image
          src={HERO_VIDEO_POSTER}
          alt="Assistir demonstração do Meu Barbeiro PRO no YouTube"
          width={640}
          height={360}
          priority
          sizes="(max-width: 768px) 90vw, 480px"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            objectFit: "contain",
          }}
        />
      </Box>
    );
  }

  if (isPlaying) {
    return (
      <Box position="relative" w="100%" sx={{ aspectRatio: "16 / 9" }} bg="barber.900">
        <iframe
          title="Demonstração do Meu Barbeiro PRO"
          src={getHeroYoutubeEmbedUrl(true)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      </Box>
    );
  }

  return (
    <Box position="relative" w="100%">
      <Image
        src={HERO_VIDEO_POSTER}
        alt="Demonstração do painel do Meu Barbeiro PRO"
        width={640}
        height={360}
        priority
        sizes="(max-width: 768px) 90vw, 480px"
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          objectFit: "contain",
          aspectRatio: "16 / 9",
          backgroundColor: "#1b1c29",
        }}
      />

      <Flex
        position="absolute"
        inset={0}
        align="center"
        justify="center"
        bg="blackAlpha.450"
        cursor="pointer"
        onClick={handlePlay}
        role="presentation"
      >
        <IconButton
          aria-label="Reproduzir demonstração do Meu Barbeiro PRO"
          icon={<Play size={28} fill="currentColor" />}
          onClick={(event) => {
            event.stopPropagation();
            handlePlay();
          }}
          size="lg"
          w="64px"
          h="64px"
          borderRadius="full"
          bg="button.cta"
          color="black"
          boxShadow="0 8px 28px rgba(0, 0, 0, 0.45)"
          _hover={{ opacity: 0.9, transform: "scale(1.04)" }}
          _active={{ transform: "scale(0.98)" }}
        />
      </Flex>
    </Box>
  );
}

export function HomeHero() {
  return (
    <Box
      as="section"
      position="relative"
      py={{ base: 14, md: 20, lg: 24 }}
      overflow="hidden"
      bg="barber.400"
    >
      <Box
        position="absolute"
        inset="0"
        bgGradient="radial(circle at 20% 20%, rgba(251, 169, 49, 0.12) 0%, transparent 50%), radial(circle at 80% 0%, rgba(251, 169, 49, 0.08) 0%, transparent 45%)"
        pointerEvents="none"
        aria-hidden
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Grid
          templateColumns={{ base: "1fr", lg: "1.05fr 0.95fr" }}
          gap={{ base: 10, lg: 12 }}
          alignItems="center"
        >
          <GridItem>
            <VStack align="start" spacing={6}>
              <Text
                as="span"
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="wider"
                textTransform="uppercase"
                color="orange.900"
              >
                Sistema de gestão para barbearias
              </Text>

              <Heading
                as="h1"
                size={{ base: "2xl", md: "3xl" }}
                lineHeight="shorter"
                color="white"
              >
                {HOME_H1}
              </Heading>

              <Text fontSize={{ base: "lg", md: "xl" }} color="barber.100">
                Agenda, clientes, equipe, serviços e gestão da sua barbearia em
                um único sistema online. Reduza faltas, ganhe tempo e tenha mais
                controle do seu negócio.
              </Text>

              <HStack spacing={3} flexWrap="wrap" w="full">
                <Button
                  as={Link}
                  href={PRIMARY_CTA_HREF}
                  size="lg"
                  bg="button.cta"
                  color="black"
                  rightIcon={<ArrowRight size={16} />}
                  _hover={{ opacity: 0.85 }}
                >
                  {PRIMARY_CTA_LABEL}
                </Button>
                <Button
                  as={Link}
                  href={SECONDARY_CTA_HREF}
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.400"
                  color="white"
                  _hover={{ bg: "whiteAlpha.100" }}
                >
                  {SECONDARY_CTA_LABEL}
                </Button>
              </HStack>

              <VStack align="start" spacing={2} pt={2}>
                {HERO_PROOF.map((item) => (
                  <HStack key={item} spacing={2} align="start">
                    <CheckCircle2 size={18} color="#fba931" />
                    <Text fontSize="sm" color="barber.100">
                      {item}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </GridItem>

          <GridItem>
            <Flex justify="center">
              <Box
                position="relative"
                w="full"
                maxW={{ base: "100%", sm: "440px", lg: "480px" }}
                borderRadius="2xl"
                border="1px solid"
                borderColor="whiteAlpha.250"
                overflow="hidden"
                bg="barber.900"
                boxShadow="0 28px 60px -16px rgba(251, 169, 49, 0.28), 0 16px 40px rgba(0, 0, 0, 0.45)"
              >
                <HeroDemoMedia />
              </Box>
            </Flex>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}
